'use client'

import * as React from 'react'
import { Clock } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { cn } from './utils'

export interface TimePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  minuteStep?: number
  className?: string
  use24Hours?: boolean
  minTime?: string
  maxTime?: string
}

export function TimePicker({
  value,
  onValueChange,
  disabled = false,
  placeholder = 'Select time',
  minuteStep = 30,
  className,
  use24Hours = false,
  minTime,
  maxTime,
  ...props
}: TimePickerProps) {
  // Generate time options for the entire day
  const timeOptions = React.useMemo(() => {
    const options = []
    const totalMinutesInDay = 24 * 60

    // Convert minTime and maxTime to minutes for comparison
    const getMinutesFromTime = (timeStr: string) => {
      const [hour, minute] = timeStr.split(':').map(Number)
      return hour * 60 + minute
    }

    const minMinutes = minTime ? getMinutesFromTime(minTime) : 0
    const maxMinutes = maxTime
      ? getMinutesFromTime(maxTime)
      : totalMinutesInDay - 1

    for (let minutes = 0; minutes < totalMinutesInDay; minutes += minuteStep) {
      // Skip times outside the allowed range
      if (minutes < minMinutes || minutes > maxMinutes) {
        continue
      }

      const hour = Math.floor(minutes / 60)
      const minute = minutes % 60

      // Format time value (HH:mm)
      const timeValue = `${hour
        .toString()
        .padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

      // Format display time
      let displayTime
      if (use24Hours) {
        displayTime = timeValue
      } else {
        const period = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour % 12 || 12
        displayTime = `${displayHour}:${minute
          .toString()
          .padStart(2, '0')} ${period}`
      }

      options.push({
        value: timeValue,
        label: displayTime,
      })
    }

    return options
  }, [minuteStep, use24Hours, minTime, maxTime])

  // Format display value
  const formatDisplayTime = (timeValue: string) => {
    if (!timeValue) return ''

    const [hour, minute] = timeValue.split(':').map(Number)

    if (use24Hours) {
      return `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`
    } else {
      const period = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 || 12
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`
    }
  }

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={cn('w-[140px]', className)}>
        <SelectValue placeholder={placeholder}>
          {value ? (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{formatDisplayTime(value)}</span>
            </div>
          ) : (
            placeholder
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-white">
        {timeOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
